import { useSyncExternalStore } from 'react';

/** Shadow map filtering for the board and the arena; `off` skips the shadow pass altogether. */
export type ShadowQuality = 'off' | 'low' | 'medium' | 'high';
export type SnapTurnDegrees = 30 | 45 | 90;
/** The board's colours: the classic look, or hues and brightnesses that colour-blind players can tell apart. */
export type PaletteId = 'classic' | 'colour-blind';
export type TabletTextSize = 'normal' | 'large';

/**
 * Player preferences: how the board looks and sounds, kept per browser. The AI's level is not one
 * of them: a game is given it as it is set up and keeps it; and with the practice fight gone, so
 * is its opponent's aim and pace (the owner, 2026-09-26).
 */
export interface Settings {
  /** Heavy smoke drifting over the board, and how thick it is (0.1 … 1). */
  fog: boolean;
  fogDensity: number;
  /** Win odds on the squares a piece can attack, and warnings under pieces the enemy could take. */
  threatHints: boolean;
  /** The guided first game: a lesson on the sign at each first occasion, each shown once. */
  guide: boolean;
  /** 0 … 1 master volume, and a mute that keeps the volume for later. */
  sound: { volume: number; muted: boolean };

  // Look and comfort, pushed to the scene together (`LookSettings`).
  /** Shadows cap what the headset does (never above `low`); reflections are the desktop's mirror. */
  graphics: { shadows: ShadowQuality; reflections: boolean };
  /**
   * VR comfort: how far a flick of the right stick turns, or whether it turns freely toward any
   * side of the view instead, and metres added to the standing height so a player on a chair sees
   * the table as if standing (−0.5 … 0.5).
   */
  comfort: { snapTurnDegrees: SnapTurnDegrees; freeTurning: boolean; seatedHeight: number };
  palette: PaletteId;
  /** Lettering on the table's tablets, `large` about a quarter bigger. */
  tabletText: TabletTextSize;
}

/** The settings the scene applies itself: everything about how the table looks and feels. */
export type LookSettings = Pick<Settings, 'graphics' | 'comfort' | 'palette' | 'tabletText'>;

export const DEFAULT_SETTINGS: Settings = {
  fog: true,
  fogDensity: 0.8,
  threatHints: true,
  guide: true,
  sound: { volume: 0.8, muted: false },
  graphics: { shadows: 'medium', reflections: true },
  comfort: { snapTurnDegrees: 30, freeTurning: false, seatedHeight: 0 },
  palette: 'classic',
  tabletText: 'normal',
};

/** How far the AI looks ahead, in plies; the labels are what the tablet and the menu show. */
/**
 * How far the board AI looks ahead. Named for how well it plays rather than how long it takes,
 * and named the same four ways as the arena skill beside it, so one word means one standard of
 * opponent wherever a player meets it.
 */
export const AI_DEPTHS: { depth: number; label: string; hint: string }[] = [
  { depth: 1, label: 'Novice', hint: 'answers at once, half by chance' },
  { depth: 2, label: 'Fair', hint: 'thinks a moment' },
  { depth: 3, label: 'Expert', hint: 'a few seconds' },
  { depth: 4, label: 'Master', hint: 'a few seconds, sees fights through' },
];

/** The board AI's level where a game is started without choosing one: Play vs AI, and the AI seats of a Custom game as it opens (Fair). */
export const DEFAULT_AI_DEPTH = 2;

export const SHADOW_QUALITIES: readonly ShadowQuality[] = ['off', 'low', 'medium', 'high'];
/** What the settings screens call each choice, on a page and on the headset's tablet alike. */
export const SHADOW_LABELS: Record<ShadowQuality, string> = {
  off: 'Off',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};
export const PALETTE_LABELS: Record<PaletteId, string> = {
  classic: 'Classic',
  'colour-blind': 'Colour-blind',
};
export const TEXT_LABELS: Record<TabletTextSize, string> = { normal: 'Normal', large: 'Large' };
export const SNAP_TURN_CHOICES: readonly SnapTurnDegrees[] = [30, 45, 90];
export const PALETTE_IDS: readonly PaletteId[] = ['classic', 'colour-blind'];
export const TABLET_TEXT_SIZES: readonly TabletTextSize[] = ['normal', 'large'];
/** The seated-height offset runs from −this to +this, in metres. */
export const SEATED_HEIGHT_RANGE = 0.5;

const STORAGE_KEY = 'archon3d.settings';

const clamp01 = (v: unknown, fallback: number) =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fallback;

const oneOf = <T>(choices: readonly T[], v: unknown, fallback: T): T =>
  choices.includes(v as T) ? (v as T) : fallback;

const bool = (v: unknown, fallback: boolean) => (typeof v === 'boolean' ? v : fallback);

/** Zero is mute; raising the slider also clears a mute saved by an older version. */
export function soundFromSlider(volume: number): Settings['sound'] {
  const next = clamp01(volume, DEFAULT_SETTINGS.sound.volume);
  return { volume: next, muted: next === 0 };
}

/** Fills in whatever an older or hand-edited copy lacks, so a stale entry never breaks the game. */
export function normalizeSettings(raw: unknown): Settings {
  const r = (raw ?? {}) as Partial<Record<keyof Settings, unknown>>;
  const sound = (r.sound ?? {}) as Partial<Settings['sound']>;
  const graphics = (r.graphics ?? {}) as Partial<Settings['graphics']>;
  const comfort = (r.comfort ?? {}) as Partial<Settings['comfort']>;
  const seated =
    typeof comfort.seatedHeight === 'number' && Number.isFinite(comfort.seatedHeight)
      ? Math.min(SEATED_HEIGHT_RANGE, Math.max(-SEATED_HEIGHT_RANGE, comfort.seatedHeight))
      : DEFAULT_SETTINGS.comfort.seatedHeight;
  return {
    fog: bool(r.fog, DEFAULT_SETTINGS.fog),
    fogDensity: Math.max(0.1, clamp01(r.fogDensity, DEFAULT_SETTINGS.fogDensity)),
    threatHints: typeof r.threatHints === 'boolean' ? r.threatHints : DEFAULT_SETTINGS.threatHints,
    guide: typeof r.guide === 'boolean' ? r.guide : DEFAULT_SETTINGS.guide,
    sound: {
      volume: clamp01(sound.volume, DEFAULT_SETTINGS.sound.volume),
      muted: bool(sound.muted, DEFAULT_SETTINGS.sound.muted),
    },
    graphics: {
      shadows: oneOf(SHADOW_QUALITIES, graphics.shadows, DEFAULT_SETTINGS.graphics.shadows),
      reflections: bool(graphics.reflections, DEFAULT_SETTINGS.graphics.reflections),
    },
    comfort: {
      snapTurnDegrees: oneOf(
        SNAP_TURN_CHOICES,
        comfort.snapTurnDegrees,
        DEFAULT_SETTINGS.comfort.snapTurnDegrees,
      ),
      freeTurning: bool(comfort.freeTurning, DEFAULT_SETTINGS.comfort.freeTurning),
      seatedHeight: seated,
    },
    palette: oneOf(PALETTE_IDS, r.palette, DEFAULT_SETTINGS.palette),
    tabletText: oneOf(TABLET_TEXT_SIZES, r.tabletText, DEFAULT_SETTINGS.tabletText),
  };
}

export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStorage(): SettingsStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Holds the settings for the running app and mirrors them to storage. React reads it through
 * `useSettings`; the scene and the session get the values pushed by the app.
 */
export class SettingsStore {
  private state: Settings;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly storage: SettingsStorage | null = browserStorage()) {
    let stored: unknown;
    try {
      const text = storage?.getItem(STORAGE_KEY);
      stored = text ? JSON.parse(text) : null;
    } catch {
      stored = null;
    }
    this.state = normalizeSettings(stored);
  }

  get(): Settings {
    return this.state;
  }

  set(patch: Partial<Settings>): void {
    this.state = normalizeSettings({ ...this.state, ...patch });
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // Private mode or a full store: the settings last for this visit.
    }
    for (const listener of this.listeners) listener();
  }

  reset(): void {
    this.set(DEFAULT_SETTINGS);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export function useSettings(store: SettingsStore): Settings {
  return useSyncExternalStore(
    (listener) => store.subscribe(listener),
    () => store.get(),
    () => store.get(),
  );
}
